import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from './Modal';
import { NumberField } from './NumberField';
import { ProductImageGallery } from './ProductImageGallery';
import { basalamService } from '../services/basalamService';
import { fieldClass, labelClass, parseNumber, slugify } from '../utils/form';
import type { BasalamProductRow } from '../services/basalamService';
import type { Category } from '../types';

type ImportForm = {
  name: string;
  slug: string;
  sku: string;
  categoryId: string;
  price: string;
  stock: string;
  shortDescription: string;
  description: string;
  isActive: boolean;
};

type Props = {
  product: BasalamProductRow | null;
  categories: Category[];
  onClose: () => void;
  onCreated?: () => void;
};

function formFromBasalam(row: BasalamProductRow): ImportForm {
  const name = cleanBasalamTitle(row.title);
  // Do not embed Basalam product id in customer-visible SKU.
  const sku = `BSL-${row.basalamProductId}`;
  return {
    name,
    slug: slugify(name),
    sku,
    categoryId: '',
    price: row.price > 0 ? String(row.price) : '',
    stock: String(row.inventory ?? 0),
    shortDescription: '',
    description: '',
    isActive: row.available,
  };
}

/** Strip Basalam «ارسال رایگان» marketing suffix from titles. */
function cleanBasalamTitle(title: string): string {
  let s = title.trim();
  const patterns = [
    /[\(\[\{]\s*ارسال\s*رایگان\s*[\)\]\}]/gi,
    /[\s\-_|,،]*ارسال\s*رایگان\s*$/gi,
  ];
  let prev = "";
  while (s !== prev) {
    prev = s;
    for (const re of patterns) s = s.replace(re, "");
    s = s.replace(/\s{2,}/g, " ").replace(/^[\s\-_|،,]+|[\s\-_|،,]+$/g, "").trim();
  }
  return s;
}

export function BasalamImportProductModal({ product, categories, onClose, onCreated }: Props) {
  const [form, setForm] = useState<ImportForm>(() => (product ? formFromBasalam(product) : emptyForm()));
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [importStats, setImportStats] = useState<{ images: number; reviews: number; users: number } | null>(null);

  useEffect(() => {
    if (product) {
      setForm(formFromBasalam(product));
      setSlugTouched(false);
      setCreatedId(null);
      setImportStats(null);
    }
  }, [product]);

  const handleClose = () => {
    setCreatedId(null);
    setImportStats(null);
    onClose();
  };

  const handleSave = async () => {
    if (!product) return;
    if (!form.name.trim()) {
      toast.error('نام محصول را وارد کنید');
      return;
    }
    if (!form.sku.trim()) {
      toast.error('SKU را وارد کنید');
      return;
    }
    if (!form.categoryId) {
      toast.error('دسته‌بندی را انتخاب کنید');
      return;
    }

    setSaving(true);
    try {
      const created = await basalamService.importProduct({
        basalamProductId: product.basalamProductId,
        name: form.name.trim(),
        slug: form.slug.trim() || slugify(form.name),
        sku: form.sku.trim(),
        categoryId: Number(form.categoryId),
        price: parseNumber(form.price),
        stock: parseNumber(form.stock),
        shortDescription: form.shortDescription.trim(),
        description: form.description.trim(),
        isActive: form.isActive,
        imageUrl: product.imageUrl,
      });
      const images = created.imagesAdded ?? 0;
      const reviews = created.reviewsAdded ?? 0;
      const users = created.usersAdded ?? 0;
      setImportStats({ images, reviews, users });
      toast.success(`محصول ایجاد شد — ${images} تصویر و ${reviews} نظر وارد شد`);
      setCreatedId(String(created.product?.id ?? ''));
      onCreated?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ایجاد محصول ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  const handleNameChange = (name: string) => {
    setForm((f) => ({
      ...f,
      name,
      slug: !slugTouched ? slugify(name) : f.slug,
    }));
  };

  return (
    <Modal
      isOpen={Boolean(product)}
      onClose={handleClose}
      title={createdId ? 'محصول ایجاد شد' : 'افزودن به محصولات سایت'}
      size="xl"
      footer={
        createdId ? (
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50"
          >
            بستن
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ایجاد و دریافت تصاویر/نظرات…' : 'تایید و ایجاد محصول'}
            </button>
          </>
        )
      }
    >
      {product ? (
        <div className="space-y-4" dir="rtl">
          {!createdId ? (
            <>
              <p className="rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
                اطلاعات از باسلام (ID: {product.basalamProductId}) پر شده — با تایید، تصاویر محصول و نظرات متنی
                (بدون عکس نظرات) هم وارد می‌شوند.
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className={labelClass}>نام محصول</label>
                  <input
                    className={fieldClass}
                    value={form.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    dir="rtl"
                  />
                </div>
                <div>
                  <label className={labelClass}>Slug</label>
                  <input
                    className={fieldClass}
                    value={form.slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setForm((f) => ({ ...f, slug: e.target.value }));
                    }}
                    dir="ltr"
                  />
                </div>
                <div>
                  <label className={labelClass}>SKU</label>
                  <input
                    className={fieldClass}
                    value={form.sku}
                    onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
                    dir="ltr"
                  />
                </div>
                <div>
                  <label className={labelClass}>دسته‌بندی</label>
                  <select
                    className={fieldClass}
                    value={form.categoryId}
                    onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
                  >
                    <option value="">انتخاب دسته</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <NumberField
                  label="قیمت (تومان)"
                  value={form.price}
                  onChange={(v) => setForm((f) => ({ ...f, price: v }))}
                />
                <NumberField
                  label="موجودی"
                  value={form.stock}
                  onChange={(v) => setForm((f) => ({ ...f, stock: v }))}
                />
                <div className="md:col-span-2">
                  <label className={labelClass}>توضیح کوتاه</label>
                  <textarea
                    className={fieldClass}
                    rows={2}
                    value={form.shortDescription}
                    onChange={(e) => setForm((f) => ({ ...f, shortDescription: e.target.value }))}
                    dir="rtl"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>توضیحات</label>
                  <textarea
                    className={fieldClass}
                    rows={4}
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    dir="rtl"
                  />
                </div>
                <div className="md:col-span-2 flex items-center gap-2">
                  <input
                    id="import-product-active"
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="import-product-active" className="text-sm font-medium text-slate-700">
                    فعال (نمایش در سایت)
                  </label>
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-emerald-700">
                محصول «{form.name}» ثبت شد
                {importStats
                  ? ` — ${importStats.images} تصویر، ${importStats.reviews} نظر، ${importStats.users} کاربر باسلام.`
                  : '.'}{' '}
                در صورت نیاز می‌توانید تصاویر را ویرایش کنید:
              </p>
              {createdId ? <ProductImageGallery productId={createdId} /> : null}
            </>
          )}
        </div>
      ) : null}
    </Modal>
  );
}

function emptyForm(): ImportForm {
  return {
    name: '',
    slug: '',
    sku: '',
    categoryId: '',
    price: '',
    stock: '0',
    shortDescription: '',
    description: '',
    isActive: true,
  };
}
